/* eslint-disable no-param-reassign */
/* eslint-disable no-underscore-dangle */
/* eslint-disable no-await-in-loop */
/* eslint-disable @typescript-eslint/no-loop-func */
import { DropzoneFile, DropzoneOptions } from 'dropzone';
import { SalesAPIService } from 'shared/api/sales-api.service';
import { DeepPartial } from 'src/app/@shared-types';
import { NumberConversionService } from '../../../shared/converter/number-converter.service';
import { FileService } from '../../../shared/storage/file.service';
import { EStaticFileType } from '../../../src/app/@shared-types/misc/file';
import { AutoboardVehicle } from '../../../src/app/@shared-types/vehicle';
import { VehicleUpdateResult } from '../../../src/app/@shared-types/vehicle/requests';
import LogService from '../../../src/app/shared/services/log';
import { ToastService, ToastType } from '../../../ui-components/toast/toast.service';
import { Vehicle } from '../../../src/app/shared/models/vehicle/vehicle';
import { VehicleFormComponentExitFunction } from '../../../src/app/@types/vehicle';

export class ImageUploadController {
  private dropzoneOptions: DropzoneOptions;

  private fileService: FileService;

  private logService: LogService;

  private numberConversionService: NumberConversionService;

  private toastService: ToastService;

  private vehicle!: AutoboardVehicle;

  private salesApiService: SalesAPIService;

  private vehicleModel: Vehicle;

  private exit!: (data: VehicleFormComponentExitFunction) => void;

  public sending = false;

  private dropzoneMethods!: {
    processQueue: () => void,
    getAllFiles: () => DropzoneFile[],
    removeAllFiles: () => void,
    removeFile: (file: DropzoneFile) => void,
  };

  private dropzoneCallbacks: {
    addedfile: () => void,
    removedfile: () => void,
  };

  private fileStats = {
    total: 0,
    uploaded: 0,
  };

  private vehicleImages: { url: string, staticId: string, }[] = [];

  /* @ngInject */
  constructor(
    fileService: FileService,
    logService: LogService,
    toastService: ToastService,
    numberConversionService: NumberConversionService,
    salesApiService: SalesAPIService,
    vehicle: Vehicle,
  ) {
    this.fileService = fileService;
    this.logService = logService;
    this.toastService = toastService;
    this.numberConversionService = numberConversionService;
    this.salesApiService = salesApiService;
    this.vehicleModel = vehicle;

    this.dropzoneOptions = {
      url: 'http://autoboard.app', // must provide url to silence console errors
      paramName: 'photo',
      maxFilesize: 15,
      acceptedFiles: 'image/jpeg, images/jpg, image/png',
      addRemoveLinks: true,
      autoProcessQueue: false,
      dictDefaultMessage: 'Lege hier Fahrzeugbilder per Drag & Drop ab oder klicke hier, um die Bilder auszuwählen',
      dictRemoveFile: 'Entfernen',
    };

    this.dropzoneCallbacks = {
      addedfile: () => {
        this.fileStats.total += 1;
      },
      removedfile: () => {
        this.fileStats.total -= 1;
      },
    };
  }

  private initPreviewImages = (additionalImages?: string[]): void => {
    this.vehicleImages = [];
    this.vehicle.vehicleData.images?.forEach((image) => {
      this.vehicleImages?.push({
        staticId: image,
        url: this.fileService.getStaticUrl(image, { width: '200' }),
      });
    });
    if (additionalImages) {
      additionalImages.forEach((image) => {
        this.vehicleImages?.push({
          staticId: image,
          url: this.fileService.getStaticUrl(image, { width: '200' }),
        });
      });
    }
  };

  public onDrop = (srcIndex: number, dstIndex: number): boolean => {
    if (!this.vehicleImages) return false;
    dstIndex = dstIndex > srcIndex ? dstIndex - 1 : dstIndex;
    const dropAllowed = srcIndex !== dstIndex;
    if (dropAllowed) {
      this.vehicleImages.splice(dstIndex, 0, this.vehicleImages.splice(srcIndex, 1)[0]);
      this.updateImagesForVehicle(this.vehicleImages.map((i) => i.staticId));
      return true;
    }
    return false;
  };

  public $onInit = (): void => {
    this.initPreviewImages();
  };

  public removeImage = async (
    staticId: string,
  ): Promise<void> => {
    if (!this.vehicle.vehicleData.images) return;
    const indexOfImage = this.vehicle.vehicleData.images.indexOf(staticId);
    this.vehicle.vehicleData.images.splice(indexOfImage, 1);
    this.initPreviewImages();
    await this.updateImagesForVehicle(this.vehicleImages.map((i) => i.staticId));
    this.fileService.deleteStatic(staticId);
  };

  private updateImagesForVehicle = (
    images: string[],
  ): Promise<VehicleUpdateResult> => {
    const changedVals: DeepPartial<AutoboardVehicle> = {
      _id: this.vehicle._id,
      vehicleData: {
        images,
      },
    };
    return this.salesApiService.putVehicle(changedVals, true);
  };

  public uploadQueue = async (): Promise<void> => {
    this.sending = true;
    const files = this.dropzoneMethods.getAllFiles();
    if (!files.length) return;

    let error = false;
    const uploadedImages: string[] = [];

    for (const file of files) {
      const progressBarElem = file.previewElement.getElementsByClassName('dz-upload');
      const progressBarId = this.numberConversionService.generateUUID();
      progressBarElem[0].id = progressBarId;

      const staticId = await this.fileService.apiUploadStatic(file, {
        type: EStaticFileType.VEHICLE_IMAGE,
        identifier: String(this.vehicle._id),
      }, progressBarId)
        .catch((e) => {
          this.logService.error(e);
          error = true;
        });
      if (staticId) uploadedImages.push(staticId);
      this.dropzoneMethods.removeFile(file);
      this.fileStats.uploaded += 1;
    }

    this.fileStats = {
      total: 0,
      uploaded: 0,
    };

    this.sending = false;

    if (!error) {
      this.initPreviewImages(uploadedImages);
      await this.updateImagesForVehicle(this.vehicleImages.map((i) => i.staticId));
      this.toastService.toast(ToastType.SUCCESS, 'Hochladen der Fahrzeugbilder erfolgreich');
      this.dropzoneMethods.removeAllFiles();
    } else {
      this.toastService.toast(ToastType.ERROR, 'Fehler beim Hochladen der Fahrzeugbilder.');
    }
  };

  public regenImages = async (): Promise<void> => {
    this.sending = true;
    await this.vehicleModel.regenerateImages(String(this.vehicle._id))
      .catch((e) => {
        this.sending = false;
        this.toastService.toast(ToastType.ERROR, 'Fehler beim Generieren der Bilder');
      });

    this.exit({ removeLock: true });
  };
}
